import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { ClientProfileFields } from "../components/client/ClientProfileFields";
import { useAuth } from "../context/AuthContext";
import { getHomePathForRole } from "../modules/registry";
import { LOGO_URL, PORTAL_NAME } from "../constants/brand";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageWatermark } from "../components/shared/PageWatermark";

const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", phone: "", mailing_address: "",
  business_name: "", business_address: "",
  payment_bank_name: "", payment_account_holder_name: "", payment_account_number: "", payment_routing_number: "", account_type: "",
  legal_name: "", tax_id: "", tax_id_type: "", tax_mailing_address: "",
};

export default function ClientProfileSetupPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const isAffiliate = user?.role === "affiliate";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/client/profile", { ...form, tax_id_type: form.tax_id_type || null, account_type: form.account_type || null });
      toast.success("Profile complete — welcome to IGC");
      const freshUser = await refreshUser();
      navigate(getHomePathForRole(freshUser?.role, freshUser?.unlocked_modules), { replace: true });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-igc-bg flex items-center justify-center p-6 relative overflow-hidden" data-testid="complete-profile-page">
      <PageWatermark variant="auth" />
      <div className="w-full max-w-2xl bg-igc-surface border border-igc-gold/20 p-10 relative z-[1]">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-16 h-16 object-contain mb-3" />
          <p className="font-serif text-igc-cream text-sm tracking-wide">{PORTAL_NAME}</p>
        </div>
        <h1 className="font-serif text-2xl text-igc-cream text-center mb-2">Complete Your Profile</h1>
        <p className="text-igc-muted text-sm text-center mb-8">
          Welcome, {user?.display_name}. Please complete your profile to continue to your dashboard. This information is stored securely and visible only to you and IGC administration.
        </p>
        <form onSubmit={submit} className="space-y-6">
          <ClientProfileFields form={form} setForm={setForm} isAffiliate={isAffiliate} />
          <Button data-testid="complete-profile-submit-button" type="submit" disabled={submitting} title="Saves your profile and takes you to your dashboard." className="w-full h-12 bg-igc-gold hover:bg-igc-goldHover text-black">
            {submitting ? "Saving..." : "Save & Continue to Dashboard"}
          </Button>
        </form>
        <div className="flex items-center justify-center gap-2 mt-6 text-igc-muted">
          <ShieldCheck size={14} weight="fill" className="text-igc-success" />
          <span className="text-[11px] uppercase tracking-widest">256-bit SSL Secured — Encrypted at Rest</span>
        </div>
      </div>
    </div>
  );
}
